import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";
import { productService } from "../../services/productService";
import { salesService } from "../../services/salesService";

const AppLayout = ({ children, pageTitle = "Manajemen Inventaris" }) => {
  // Mengambil data user dan fungsi logout dari "Ingatan Global" (AuthContext)
  const { user, signOut, updateProfile } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  // --- MEMORI (STATE): Kelengkapan Profil ---
  const [profileFormData, setProfileFormData] = useState({
    fullName: user?.user_metadata?.full_name || "",
    storeName: user?.user_metadata?.store_name || "",
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Periksa apakah profil sudah lengkap (hanya jika ada user yang sedang login)
  const isProfileComplete = !user || (user.user_metadata?.full_name && user.user_metadata?.store_name);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!profileFormData.fullName || !profileFormData.storeName) return;
    setIsUpdatingProfile(true);
    try {
      const { error } = await updateProfile(user.id, profileFormData.fullName, profileFormData.storeName);
      if (error) throw error;
      showNotification({ type: 'success', title: 'Profil Diperbarui', message: 'Profil Anda telah lengkap.' });
      window.location.reload(); 
    } catch (err) {
      console.error(err);
      showNotification({ type: 'error', title: 'Pembaruan Gagal', message: err.message || "Gagal memperbarui profil." });
      setIsUpdatingProfile(false);
    }
  };

  // --- MEMORI (STATE): Mengatur buka-tutup menu dan pencarian ---
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // --- MEMORI (STATE): Segala hal tentang kolom pencarian ---
  const [searchQuery, setSearchQuery] = useState(""); // teks yang sedang diketik
  const [searchResults, setSearchResults] = useState([]); // hasil yang ditemukan
  const [isSearching, setIsSearching] = useState(false); // status sedang mencari (loading)
  const [showDropdown, setShowDropdown] = useState(false); // apakah kotak hasil pencarian muncul?

  /**
   * --- LOGIKA: Fungsi Pencarian Utama ---
   * Fungsi ini akan memanggil 'Layanan' (Service) untuk mencari barang atau transaksi
   * berdasarkan teks yang diketik pengguna.
   */
  const performSearch = useCallback(async () => {
    setIsSearching(true);
    setShowDropdown(true);
    try {
      const products = await productService.searchProducts(searchQuery, 3);
      const sales = await salesService.searchSales(searchQuery, 3);

      const combinedResults = [
        ...(products || []).map((p) => ({ ...p, type: "product" })),
        ...(sales || []).map((s) => ({ ...s, type: "sale" })),
      ];

      setSearchResults(combinedResults);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  /**
   * --- LOGIKA OTOMATIS: Debounce Search ---
   * Memberi jeda 0.4 detik saat pengguna mengetik sebelum mulai mencari.
   * Ini agar aplikasi tidak terlalu sibuk jika pengguna mengetik sangat cepat.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        performSearch();
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  // Tutup kotak pencarian secara otomatis jika pindah halaman
  useEffect(() => {
    setShowDropdown(false);
    setSearchQuery("");
  }, [location.pathname]);

  /**
   * --- LOGIKA: Keluar Akun ---
   * Memunculkan kotak konfirmasi sebelum benar-benar mengeluarkan pengguna.
   */
  const handleSignOut = () => {
    showNotification({
      type: "confirm",
      title: "Konfirmasi Keluar",
      message: "Apakah Anda yakin ingin keluar?",
      onConfirm: async () => {
        try {
          await signOut();
          navigate("/login");
        } catch (error) {
          console.error("Error signing out:", error);
        }
      },
    });
  };

  // Daftar menu navigasi di samping
  const navItems = [
    { name: "Dashboard", path: "/dashboard", icon: "dashboard" },
    { name: "Inventaris", path: "/inventory", icon: "inventory_2" },
    { name: "Buat Penjualan", path: "/new-sale", icon: "receipt_long" },
    { name: "Riwayat Jual", path: "/sales-history", icon: "history" },
  ];

  // Bagian tampilan untuk kolom pencarian
  const searchMarkup = (
    <div className="relative w-full max-w-md">
      <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-lg pointer-events-none">
        search
      </span>
      <input
        className="w-full pl-10 pr-4 py-2 bg-surface-container-highest border-none rounded-md text-sm focus:ring-2 focus:ring-primary-fixed-dim focus:bg-surface-container-lowest transition-all"
        placeholder="Cari stok, SKU, atau penjualan..."
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => {
          if (searchQuery.trim().length >= 2) setShowDropdown(true);
        }}
        autoFocus={isMobileSearchOpen}
      />

      {/* TAMPILAN: Kotak Hasil Pencarian (Dropdown) */}
      {showDropdown && (
        <div className="absolute top-12 left-0 w-full bg-white dark:bg-slate-900 border border-outline-variant/30 rounded-xl shadow-lg shadow-black/5 overflow-hidden z-50 ambient-bloom">
          <div className="max-h-80 overflow-y-auto">
            {isSearching ? (
              <div className="p-4 flex items-center justify-center gap-2 text-on-surface-variant">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Mencari...</span>
              </div>
            ) : searchResults.length > 0 ? (
              <ul className="py-2">
                {searchResults.map((item, idx) => (
                  <li key={idx}>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        setSearchQuery("");
                        setIsMobileSearchOpen(false);
                        if (item.type === "product") {
                          navigate(`/inventory/edit?id=${item.id}`);
                        } else {
                          navigate(`/sales-receipt?id=${item.id}`);
                        }
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-surface-container-low transition-colors flex flex-col gap-1 border-b border-outline-variant/10 last:border-0"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-bold text-on-surface">
                          {item.type === "product"
                            ? item.name
                            : `#${item.transaction_id}`}
                        </span>
                        <span
                          className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-md ${
                            item.type === "product"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {item.type === "product" ? "Stok" : "Penjualan"}
                        </span>
                      </div>
                      <span className="text-xs text-on-surface-variant">
                        {item.type === "product"
                          ? `SKU: ${item.sku}`
                          : `Pelanggan: ${item.customer_name || "Umum"}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-center text-sm text-on-surface-variant">
                Tidak ada hasil ditemukan untuk "{searchQuery}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-surface text-on-surface font-body antialiased min-h-screen">
      {/* --- OVERLAY LENGKAPI PROFIL --- */}
      {!isProfileComplete && (
        <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest max-w-md w-full rounded-2xl p-8 shadow-2xl ring-1 ring-outline-variant/30 ambient-bloom">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-primary text-3xl">storefront</span>
              </div>
              <h2 className="font-headline text-2xl font-bold text-on-surface mb-2">Lengkapi Profil Anda</h2>
              <p className="text-sm text-on-surface-variant">Sebelum dapat mengakses sistem inventaris, mohon lengkapi data toko Anda terlebih dahulu.</p>
            </div>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="space-y-1.5">
                <label className="font-label text-[11px] uppercase tracking-wider font-semibold text-on-surface-variant px-1" htmlFor="profileFullName">
                  Nama Anda
                </label>
                <input
                  id="profileFullName"
                  className="w-full bg-surface-container-highest border-none rounded-md px-4 py-3 text-on-surface focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none"
                  placeholder="Mis. John Doe"
                  value={profileFormData.fullName}
                  onChange={(e) => setProfileFormData({ ...profileFormData, fullName: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="font-label text-[11px] uppercase tracking-wider font-semibold text-on-surface-variant px-1" htmlFor="profileStoreName">
                  Nama Toko
                </label>
                <input
                  id="profileStoreName"
                  className="w-full bg-surface-container-highest border-none rounded-md px-4 py-3 text-on-surface focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none"
                  placeholder="Mis. Toko Maju Jaya"
                  value={profileFormData.storeName}
                  onChange={(e) => setProfileFormData({ ...profileFormData, storeName: e.target.value })}
                  required
                />
              </div>
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isUpdatingProfile}
                  className="w-full primary-gradient text-on-primary font-headline font-bold py-3.5 rounded-md hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isUpdatingProfile ? "Menyimpan..." : "Simpan Profil"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Overlay (Menghitamkan latar saat menu HP dibuka) */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* --- PANEL NAVBAR SAMPING --- */}
      <aside
        className={`fixed left-0 top-0 h-screen w-64 border-r-0 bg-slate-50 dark:bg-slate-900 flex flex-col py-6 z-50 transition-transform duration-300 transform lg:translate-x-0 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-6 mb-10 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold font-manrope text-slate-900 dark:text-white truncate max-w-[200px]" title={user?.user_metadata?.store_name || "Harapan Kita"}>
              {user?.user_metadata?.store_name || "Harapan Kita"}
            </h1>
            <p className="text-xs text-slate-500 font-medium tracking-tight">
              Inventaris Admin
            </p>
          </div>
          <button
            className="lg:hidden p-2 text-slate-500 hover:text-slate-900"
            onClick={() => setSidebarOpen(false)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Daftar Link Navigasi */}
        <nav className="flex-1 px-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-colors duration-200 ease-in-out font-manrope text-sm font-medium tracking-tight ${
                  isActive
                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/50 dark:hover:bg-slate-800/50"
                }`
              }
              to={item.path}
              onClick={() => setSidebarOpen(false)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              <span>{item.name}</span>
            </NavLink>
          ))}
        </nav>

        {/* Bagian Bawah: Profil User & Tombol Keluar */}
        <div className="px-4 mt-auto">
          <button
            onClick={handleSignOut}
            className="flex items-center w-full gap-3 px-4 py-2 text-error hover:bg-error/10 mx-2 rounded-lg transition-all duration-200 ease-in-out font-manrope text-sm font-medium tracking-tight text-left mb-4"
          >
            <span className="material-symbols-outlined">logout</span>
            <span>Keluar</span>
          </button>

          <div className="mt-4 flex items-center gap-3 px-4 py-2 bg-slate-100 dark:bg-slate-800/50 rounded-xl mx-2">
            <div className="w-8 h-8 rounded-full bg-primary-fixed flex items-center justify-center overflow-hidden">
              <img
                alt="User Profile"
                className="w-full h-full object-cover"
                src={
                  user?.user_metadata?.avatar_url ||
                  "https://lh3.googleusercontent.com/aida-public/AB6AXuBFMmafGc4Bm9oAVe7eVNH4pnLMjvQ5bIkpSHrdnNeXPK6LLSLuXR6C8cZRTIoJJX7fthIYYR_zEsvSzsd40-yy9ka5mnARqzXyEnvlZodqKDRIBPk0mczIQdw-AYUB2vZHyz6PE3J6iO2Z1cx5TYQAdMnltg9x4-MyyNFg3d3FQAeaHpCch5TZtwrdABbVs20_KiceOU5EKtJ3TkCv3dgt0x6jY-GFYC_nK3w8HIq6EV55SThK_c3MgAh11EIKXzI6iI80t7Rl1Xs"
                }
              />
            </div>
            <div className="overflow-hidden">
              <p className="text-xs font-bold text-on-surface truncate">
                {user?.user_metadata?.full_name || "Admin User"}
              </p>
              <p className="text-[10px] text-on-surface-variant truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* --- AREA KONTEN UTAMA --- */}
      <main className="lg:ml-64 min-h-screen bg-surface flex flex-col overflow-x-hidden">
        {/* Header Atas (TopNavBar) */}
        <header className="sticky top-0 z-40 w-full bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200/30 dark:border-slate-800/30 shadow-sm dark:shadow-none flex items-center justify-between px-4 md:px-8 h-16 transition-all duration-300">
          {isMobileSearchOpen ? (
            <div className="flex md:hidden items-center w-full gap-2 transition-all">
              {searchMarkup}
              <button
                onClick={() => {
                  setIsMobileSearchOpen(false);
                  setSearchQuery("");
                }}
                className="p-2 text-slate-500 hover:text-error shrink-0 flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-xl">close</span>
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 md:gap-4 flex-1 transition-all">
                <button
                  className="lg:hidden p-2 text-slate-500 hover:text-slate-900 flex items-center justify-center"
                  onClick={() => setSidebarOpen(true)}
                >
                  <span className="material-symbols-outlined">menu</span>
                </button>
                <div className="hidden md:block flex-1">{searchMarkup}</div>
              </div>
              <div className="flex items-center gap-1 md:gap-4 transition-all">
                <button
                  onClick={() => setIsMobileSearchOpen(true)}
                  className="md:hidden p-2 text-slate-500 hover:text-blue-600 transition-all duration-300 flex items-center justify-center"
                >
                  <span className="material-symbols-outlined">search</span>
                </button>
                <div className="h-6 w-[1px] bg-outline-variant/30 mx-1"></div>
                <div className="flex items-center px-1">
                  <span className="text-[11px] md:text-sm font-inter text-on-surface-variant font-bold whitespace-nowrap uppercase tracking-wider">
                    {pageTitle}
                  </span>
                </div>
              </div>
            </>
          )}
        </header>

        {/* Tempat Menampilkan Halaman yang Dipilih */}
        <div className="p-4 md:p-6 lg:p-10 flex-1">{children}</div>
      </main>
    </div>
  );
};

export default AppLayout;
